import React, {Component} from "react";
import {connect} from "react-redux";
import {Translate, translate} from "react-i18nify";

import * as componentTypes from "../../../constants/componentTypes";
import * as databaseStatuses from "../../../constants/databaseStatuses";
import * as modelHelper from "../../../helpers/modelHelper";
import * as modules from "../../../constants/modules";
import * as navigationActions from "../../../actions/navigationActions";
import * as partialUpdatesActions from "../../../actions/partialUpdatesActions";
import * as resources from "../../../constants/resources";
import * as serverActions from "../../../actions/serverActions";
import * as translationHelper from "../../../helpers/translationHelper";

import imgConfirm from "../../../resources/img/svgicons/confirm.svg";
import imgDb from "../../../resources/img/db.png";
import imgError from "../../../resources/img/svgicons/cancel.svg";
import imgPartialUpdate from "../../../resources/img/svgicons/bolt.svg";

import Button, {BUTTON_SMALL, BUTTON_VARIANT_WARNING} from "../generic/Button";
import CancelButton from "../generic/CancelButton";
import Modal from "../generic/Modal";
import imgSuccess from "../../../resources/img/svgicons/circle-check-solid.svg";
import imgWarning from "../../../resources/img/svgicons/warning-fill.svg";
import {ReactSVG} from "react-svg";

class ChangeDatabaseModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedDatabase: this.props.activeDatabase
        };
    }

    componentDidMount = () => {
        this.props.dispatchGetPartialUpdatesCountAction();
    };

    handleOpenPartialUpdatesModalClick = () => {
        window.storeProvider.clearPartialUpdate(); //stop background timer
        this.props.dispatchOpenPartialUpdatesModalAction();
        this.props.dispatchCloseChangeDatabaseModalAction();
    };

    handleCloseModalClick = () => {
        this.props.dispatchCloseChangeDatabaseModalAction();
    };

    handleCancelClick = () => {
        this.props.dispatchCloseChangeDatabaseModalAction();
    };

    getDatabaseByName = (databaseName) => {
        let database;
        for (database of this.props.databaseList) {
            if (database.name === databaseName) {
                return database;
            }
        }

        for (database of this.props.invalidDatabaseList) {
            if (database.name === databaseName) {
                return database;
            }
        }

        return null;
    };

    handleDatabaseChange = (event) => {
        this.setState({
            selectedDatabase: this.getDatabaseByName(event.currentTarget.dataset.name)
        });
    };

    handleSaveDatabaseClick = () => {
        if (
            this.props.activeDatabase === null ||
            this.state.selectedDatabase === null ||
            this.state.selectedDatabase.name === this.props.activeDatabase.name
        ) {
            this.props.dispatchCloseChangeDatabaseModalAction();
        } else {
            this.props.dispatchChangeDatabaseAction(this.state.selectedDatabase.name);
        }
    };

    handleDatabaseDoubleClick = () => {
        if (this.state.selectedDatabase.status === databaseStatuses.OK) {
            this.handleSaveDatabaseClick();
        }
        return null;
    };

    getDatabaseButton = (database) => {
        const image = window.imageStore.getDbImageByName(database.name);
        const img = (
            <img
                className="height-3vh"
                src={image ? "data:" + image.type + ";base64," + image.img : imgDb}
                alt="database"
                aria-hidden="true"
            />
        );

        return (
            <Button
                className={"width-100" + (database.name === this.state.selectedDatabase.name ? " active" : "")}
                key={database.name}
                label={database.name}
                onClick={this.handleDatabaseChange}
                imgBefore={img}
                disabled={database.status !== databaseStatuses.OK}
                onDoubleClick={this.handleDatabaseDoubleClick}
                elementProps={{
                    "data-name": database.name
                }}
            />
        );
    };

    getErrorDatabaseButton = (database) => {
        return (
            <Button
                className={"width-100" + (database.name === this.state.selectedDatabase.name ? " active" : "")}
                key={database.name}
                label={database.name}
                onClick={this.handleDatabaseChange}
                imgBefore={<img className="height-3vh" src={imgError} alt="database" aria-hidden="true" />}
                elementProps={{
                    "data-name": database.name
                }}
            />
        );
    };

    getSelectedDatabaseDetail = () => {
        if (this.state.selectedDatabase === null) {
            return null;
        }

        if (this.state.selectedDatabase.status !== databaseStatuses.OK) {
            return (
                <div className="flex-child" style={{height: "max-content"}}>
                    <div className="color-error">
                        {translate("changeDatabase.databaseWarning", {databaseName: this.state.selectedDatabase.name})}:
                    </div>
                    <div className="color-black">
                        {translationHelper.getDbStatusTranslation(this.state.selectedDatabase.status)}
                    </div>
                </div>
            );
        }

        if (this.state.selectedDatabase.main === null) {
            return null;
        }

        let primaryDetail = [
            <div key="1" className="color-primary">
                {translate("general.version")}:
            </div>,
            <div key="2">{this.state.selectedDatabase.main.customerVersion}</div>,
            <div key="3" className="color-primary">
                {translate("general.versionId")}:
            </div>,
            <div key="4">{this.state.selectedDatabase.main.customerVersionId}</div>
        ];

        if (this.props.activeServer && this.props.activeServer.computerId) {
            primaryDetail.push(
                <div key="5" className="color-primary">
                    {translate("server.computerId")}:
                </div>,
                <div key="6">{this.props.activeServer.computerId}</div>
            );
        }

        if (this.state.selectedDatabase.main.installationDate !== null) {
            primaryDetail.push(
                <div key="7" className="color-primary">
                    {translate("general.installed")}:
                </div>,
                <div key="8">{this.state.selectedDatabase.main.formatInstallationDate()}</div>
            );
        }

        if (this.state.selectedDatabase.main.creationDate !== null) {
            primaryDetail.push(
                <div key="9" className="color-primary">
                    {translate("general.created")}:
                </div>,
                <div key="10">{this.state.selectedDatabase.main.formatCreationDate()}</div>
            );
        }

        let secondaryDetail = [
            <div key="11" className="color-primary margin-top-15">
                {translate("general.versionStructure")}:
            </div>,
            <div key="12" className="margin-top-15">
                {this.state.selectedDatabase.main.version}
            </div>
        ];

        if (this.state.selectedDatabase.main.shotsCoef) {
            secondaryDetail.push(
                <div key="13" className="color-primary">
                    {translate("customRedlike.shotsCoefficient")}:
                </div>,
                <div key="14">{this.state.selectedDatabase.main.shotsCoef}</div>
            );
        }

        if (this.state.selectedDatabase.main.changelog) {
            secondaryDetail.push(
                <div key="15" className="color-primary">
                    {translate("environmentsSettings.changelog")}:
                </div>,
                <span key="16" style={{whiteSpace: "pre-line"}}>
                    {this.state.selectedDatabase.main.changelog}
                </span>
            );
        }

        if (this.state.selectedDatabase.main.moreInfo) {
            secondaryDetail.push(
                <div key="17" className="color-primary">
                    {translate("general.note")}:
                </div>,
                <div key="18">{this.state.selectedDatabase.main.moreInfo}</div>
            );
        }

        const partialUpdatesDetail = () => {
            if (!this.props.partialUpdatesModule) {
                return null;
            }

            const ok = (
                <div key="partial-updates-data" className="margin-top-15 color-success flex flex-align-center-vertical">
                    <ReactSVG
                        className={"fill-success status-icon width-25 flex padding-right-5 flex react-svg-icon"}
                        src={imgSuccess}
                        alt="enabled"
                        wrapper="span"
                    />
                    {translate("general.workingProperly")}
                </div>
            );
            const unableToSynchronize = (
                <div key="partial-updates-data" className="margin-top-15 color-warning flex flex-align-center-vertical">
                    <ReactSVG
                        className={"fill-warning status-icon width-25 flex padding-right-5 flex react-svg-icon"}
                        src={imgWarning}
                        alt="enabled"
                        wrapper="span"
                    />
                    {translate("general.unableToSynchronize")}
                </div>
            );

            return (
                <React.Fragment>
                    <div
                        key="partial-updates-title"
                        className="color-primary  margin-top-15 flex flex-align-center-vertical"
                    >
                        {translate("partialUpdates.partialUpdates")}:
                    </div>
                    {this.props.databasesReadyForPartialUpdates.find(
                        (db) =>
                            this.state.selectedDatabase.masterUuid === db.masterUuid &&
                            this.state.selectedDatabase.zoneUuid === db.uuid
                    )
                        ? ok
                        : unableToSynchronize}
                </React.Fragment>
            );
        };

        return (
            <div
                className="flex-child"
                style={{
                    width: "max-content",
                    height: "max-content",
                    display: "grid",
                    gridTemplateColumns: "max-content auto",
                    columnGap: "15px"
                }}
            >
                {primaryDetail}
                {secondaryDetail}
                {partialUpdatesDetail()}
            </div>
        );
    };

    getContent = () => {
        return modelHelper.buildModalSection(
            <React.Fragment>
                <div
                    className="flex flex-column flex-gap-10px slim-scroll flex-child"
                    style={{width: "max-content", minWidth: "min-content"}}
                >
                    {this.props.databaseList.map(this.getDatabaseButton)}
                    {this.props.invalidDatabaseList.map(this.getErrorDatabaseButton)}
                </div>

                {this.getSelectedDatabaseDetail()}
            </React.Fragment>,
            "slim-scroll flex-gap-3vh flex-space-between"
        );
    };

    getFooter = () => {
        return modelHelper.buildModalSection(
            <React.Fragment>
                {this.props.loggedUser.canRead(resources.COMPONENT_PARTIAL_UPDATES_MODAL) ? (
                    <Button
                        className="align-left"
                        size={BUTTON_SMALL}
                        variant={BUTTON_VARIANT_WARNING}
                        label={translate("general.partialUpdates")}
                        tooltipText={
                            this.props.partialUpdatesAvailable
                                ? null
                                : translate("partialUpdates.noPartialUpdatesAvailable")
                        }
                        disabled={!this.props.partialUpdatesAvailable}
                        onClick={this.handleOpenPartialUpdatesModalClick}
                        svgBefore={imgPartialUpdate}
                        elementProps={{style: {maxWidth: "12vw"}}}
                    />
                ) : null}
                <Button
                    label={<Translate value="general.confirm" />}
                    svgBefore={imgConfirm}
                    tooltipText={
                        this.state.selectedDatabase
                            ? translationHelper.getDbStatusTranslation(this.state.selectedDatabase.status)
                            : null
                    }
                    disabled={this.state.selectedDatabase?.status !== databaseStatuses.OK ?? true}
                    onClick={this.handleSaveDatabaseClick}
                />
                <CancelButton onClick={this.handleCancelClick} />
            </React.Fragment>,
            "flex-gap-10px"
        );
    };

    render() {
        return (
            <Modal
                modalId={this.props.id}
                header={modelHelper.buildModalSection(<Translate value="changeDatabase.changeDatabase" />)}
                onClose={this.handleCloseModalClick}
                content={this.getContent()}
                footer={this.getFooter()}
            />
        );
    }
}

const mapStoreToProps = (store) => {
    return {
        loggedUser: store.login.loggedUser,
        activeDatabase: store.server.activeDatabase,
        activeServer: store.server.activeServer,
        databaseList: store.server.databaseList,
        invalidDatabaseList: store.server.invalidDatabaseList,
        partialUpdatesAvailable: store.global.partialUpdatesCount,
        databasesReadyForPartialUpdates: store.global.databasesReadyForPartialUpdates,
        partialUpdatesModule: store.module.modules.some((module) => module.code === modules.MODULE_PARTIAL_UPDATES)
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        dispatchCloseChangeDatabaseModalAction: () => {
            dispatch(navigationActions.closeModal(componentTypes.CHANGE_DATABASE_MODAL));
        },
        dispatchChangeDatabaseAction: (database) => {
            dispatch(serverActions.setDatabase(database));
        },
        dispatchGetPartialUpdatesCountAction: () => {
            dispatch(partialUpdatesActions.getPartialUpdatesCount());
        },
        dispatchOpenPartialUpdatesModalAction: () => {
            dispatch(partialUpdatesActions.getNewPartialUpdates());
        }
    };
};

export default ChangeDatabaseModal = connect(mapStoreToProps, mapDispatchToProps)(ChangeDatabaseModal);
